/**
 * Link Redux Slice
 *
 * Manages link/tile state with normalized data structure
 * for optimal performance and caching.
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
// Import directly from linkService to avoid module resolution issues
import {
  linkService,
  type CreateLinkData,
  type UpdateLinkData,
} from "../../services/linkService";
import { cache, CACHE_KEYS } from "../../utils/cacheUtils";
import { errorMessage } from "@jkast90/kast-common/format";
import type { NormalizedTile, TileId } from "../../types/entities";
import type { RootState } from "../store";

type Link = NormalizedTile;

/** A link's key in the store: tile_id, else id (none for a malformed one). */
const linkKey = (link: Link): TileId | undefined => link.tile_id || link.id;

const createLinkThunk = createAsyncThunk.withTypes<{
  state: RootState;
  rejectValue: string;
}>();

interface LinkState {
  links: Link[];
  selectedLink: number | string | null;
  loading: boolean;
  error: string | null;

  // Normalized structure
  entities: Record<number | string, Link>;
  ids: (number | string)[];

  // Metadata
  lastFetched: number | null;
  cacheValid: boolean;

  // Operation-specific states
  operations: {
    list: { loading: boolean; error: string | null };
    details: { loading: boolean; error: string | null };
    create: { loading: boolean; error: string | null };
    update: { loading: boolean; error: string | null };
    delete: { loading: boolean; error: string | null };
  };
}

// Hydrate from the signed-in user's localStorage cache so cached links render
// synchronously on page load, before the network round-trip completes.
// Computed lazily, so a user change (which resets this slice) re-reads the
// new user's cache instead of keeping the first one's.
function buildInitialState(): LinkState {
  const cached = cache.get<Link[]>(CACHE_KEYS.LINKS, { ttl: Infinity });
  const links = Array.isArray(cached) ? cached : [];
  const entities: Record<number | string, Link> = {};
  const ids: (number | string)[] = [];
  links.forEach((link) => {
    const id = linkKey(link);
    if (id == null) return;
    entities[id] = link;
    ids.push(id);
  });

  return {
    links,
    selectedLink: null,
    loading: false,
    error: null,

    entities,
    ids,

    lastFetched: null,
    cacheValid: links.length > 0,

    operations: {
      list: { loading: false, error: null },
      details: { loading: false, error: null },
      create: { loading: false, error: null },
      update: { loading: false, error: null },
      delete: { loading: false, error: null },
    },
  };
}

// Async thunks
export const loadLinks = createLinkThunk(
  "links/loadLinks",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // withCache returns cached data synchronously and revalidates in the
      // background — onFreshData fires when background refresh produces a
      // different result, letting us update the store without a loading flash.
      return await linkService.getLinks((freshLinks) => {
        dispatch(linkSlice.actions.setLinks(freshLinks));
      });
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to load links"));
    }
  },
);

export const loadLinksByGroup = createLinkThunk(
  "links/loadLinksByGroup",
  async (groupId: number, { dispatch, rejectWithValue }) => {
    try {
      return await linkService.getLinksByGroup(groupId, (freshLinks) => {
        dispatch(linkSlice.actions.mergeFreshGroupLinks(freshLinks));
      });
    } catch (error) {
      return rejectWithValue(
        errorMessage(error, "Failed to load links for group"),
      );
    }
  },
);

export const createLink = createLinkThunk(
  "links/createLink",
  async (
    { linkData, userEmail }: { linkData: CreateLinkData; userEmail: string },
    { rejectWithValue },
  ) => {
    try {
      return await linkService.createLink(linkData, userEmail);
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to create link"));
    }
  },
);

export const updateLink = createLinkThunk(
  "links/updateLink",
  async (
    {
      linkId,
      linkData,
      userEmail,
    }: { linkId: TileId; linkData: UpdateLinkData; userEmail?: string },
    { rejectWithValue },
  ) => {
    try {
      return await linkService.updateLink(linkId, linkData, userEmail || "");
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to update link"));
    }
  },
);

export const deleteLink = createLinkThunk(
  "links/deleteLink",
  async (linkId: number | string, { rejectWithValue }) => {
    try {
      await linkService.deleteLink(linkId);
      return linkId;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to delete link"));
    }
  },
);

export const linkToGroup = createLinkThunk(
  "links/linkToGroup",
  async (
    { groupId, tileId }: { groupId: number; tileId: number },
    { rejectWithValue },
  ) => {
    try {
      return await linkService.linkToGroup(groupId, tileId);
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to link to group"));
    }
  },
);

export const unlinkFromGroup = createLinkThunk(
  "links/unlinkFromGroup",
  async (
    { groupId, tileId }: { groupId: number; tileId: number },
    { rejectWithValue },
  ) => {
    try {
      return await linkService.unlinkFromGroup(groupId, tileId);
    } catch (error) {
      return rejectWithValue(
        errorMessage(error, "Failed to unlink from group"),
      );
    }
  },
);

const linkSlice = createSlice({
  name: "links",
  initialState: buildInitialState,
  reducers: {
    setSelectedLink: (state, action: PayloadAction<number | string | null>) => {
      state.selectedLink = action.payload;
    },
    setLinks: (state, action: PayloadAction<Link[]>) => {
      state.links = action.payload;

      // Update normalized structure
      state.entities = {};
      state.ids = [];
      action.payload.forEach((link) => {
        const id = linkKey(link);
        if (id == null) return;
        state.entities[id] = link;
        state.ids.push(id);
      });
    },
    setAllLinks: (state, action: PayloadAction<Link[]>) => {
      // Alias for setLinks to maintain compatibility
      state.links = action.payload;

      // Update normalized structure
      state.entities = {};
      state.ids = [];
      action.payload.forEach((link) => {
        const id = linkKey(link);
        if (id == null) return;
        state.entities[id] = link;
        state.ids.push(id);
      });
    },
    // Merge background-revalidated group links into existing state without
    // wiping unrelated groups' entries.
    mergeFreshGroupLinks: (state, action: PayloadAction<Link[]>) => {
      action.payload.forEach((link) => {
        const id = linkKey(link);
        if (id == null) return;
        state.entities[id] = link;
        if (!state.ids.includes(id)) state.ids.push(id);
        const index = state.links.findIndex((l) => linkKey(l) === id);
        if (index !== -1) {
          state.links[index] = link;
        } else {
          state.links.push(link);
        }
      });
      state.lastFetched = Date.now();
      state.cacheValid = true;
    },
    updateLinkInList: (
      state,
      action: PayloadAction<{
        linkId: number | string;
        updates: Partial<Link>;
      }>,
    ) => {
      const { linkId, updates } = action.payload;

      // Update in normalized entities
      if (state.entities[linkId]) {
        state.entities[linkId] = {
          ...state.entities[linkId],
          ...updates,
        };
      }

      // Update in array
      const index = state.links.findIndex((l) => linkKey(l) === linkId);
      if (index !== -1) {
        state.links[index] = { ...state.links[index], ...updates };
      }
    },
    removeLinkFromList: (state, action: PayloadAction<number | string>) => {
      const linkId = action.payload;

      // Remove from normalized structure
      delete state.entities[linkId];
      state.ids = state.ids.filter((id) => id !== linkId);

      // Remove from array
      state.links = state.links.filter((l) => linkKey(l) !== linkId);

      // Clear selection if deleted link was selected
      if (state.selectedLink === linkId) {
        state.selectedLink = null;
      }
    },
    clearError: (state) => {
      state.error = null;
      // Clear all operation errors
      Object.values(state.operations).forEach((op) => {
        op.error = null;
      });
    },
    setCacheValidity: (state, action: PayloadAction<boolean>) => {
      state.cacheValid = action.payload;
      if (!action.payload) {
        state.lastFetched = null;
      }
    },
    setLoading: (
      state,
      action: PayloadAction<{ operation: string; loading: boolean }>,
    ) => {
      const { operation, loading } = action.payload;
      if (operation === "fetchAll") {
        state.loading = loading;
        state.operations.list.loading = loading;
      } else if (operation in state.operations) {
        state.operations[operation as keyof typeof state.operations].loading =
          loading;
      }
    },
    setError: (
      state,
      action: PayloadAction<{ operation: string; error: string }>,
    ) => {
      const { operation, error } = action.payload;
      if (operation === "fetchAll") {
        state.error = error;
        state.operations.list.error = error;
      } else if (operation in state.operations) {
        state.operations[operation as keyof typeof state.operations].error =
          error;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Load links — only show loading on cold load. When we already have
      // data (from cache hydration or a prior fetch), revalidate silently so
      // the UI shows last-known links instead of a loading flash.
      .addCase(loadLinks.pending, (state) => {
        if (state.links.length === 0) {
          state.loading = true;
          state.operations.list.loading = true;
        }
      })
      .addCase(loadLinks.fulfilled, (state, action) => {
        state.loading = false;
        state.operations.list.loading = false;
        state.links = action.payload;

        // Update normalized structure
        state.entities = {};
        state.ids = [];
        action.payload.forEach((link) => {
          const id = linkKey(link);
          if (id == null) return;
          state.entities[id] = link;
          state.ids.push(id);
        });

        state.lastFetched = Date.now();
        state.cacheValid = true;
      })
      .addCase(loadLinks.rejected, (state, action) => {
        state.loading = false;
        state.operations.list.loading = false;
        state.error = action.payload ?? null;
        state.operations.list.error = action.payload ?? null;
      })

      // Load links by group — same cache-first pattern.
      .addCase(loadLinksByGroup.pending, (state) => {
        if (state.links.length === 0) {
          state.operations.list.loading = true;
        }
      })
      .addCase(loadLinksByGroup.fulfilled, (state, action) => {
        state.operations.list.loading = false;

        // Merge with existing links
        const newLinks = action.payload;
        newLinks.forEach((link) => {
          const id = linkKey(link);
          if (id == null) return;
          state.entities[id] = link;

          if (!state.ids.includes(id)) {
            state.ids.push(id);
          }

          const index = state.links.findIndex((l) => linkKey(l) === id);
          if (index !== -1) {
            state.links[index] = link;
          } else {
            state.links.push(link);
          }
        });
      })
      .addCase(loadLinksByGroup.rejected, (state, action) => {
        state.operations.list.loading = false;
        state.operations.list.error = action.payload ?? null;
      })

      // Create link
      .addCase(createLink.pending, (state) => {
        state.operations.create.loading = true;
      })
      .addCase(createLink.fulfilled, (state, action) => {
        state.operations.create.loading = false;
        const link = action.payload;
        const id = linkKey(link);

        state.links.push(link);
        if (id != null) {
          state.entities[id] = link;
          state.ids.push(id);
        }
      })
      .addCase(createLink.rejected, (state, action) => {
        state.operations.create.loading = false;
        state.operations.create.error = action.payload ?? null;
      })

      // Update link
      .addCase(updateLink.pending, (state) => {
        state.operations.update.loading = true;
      })
      .addCase(updateLink.fulfilled, (state, action) => {
        state.operations.update.loading = false;
        const updatedLink = action.payload;
        const id = linkKey(updatedLink);
        if (id == null) return;

        // Update in entities
        state.entities[id] = updatedLink;

        // Update in array
        const index = state.links.findIndex((l) => linkKey(l) === id);
        if (index !== -1) {
          state.links[index] = updatedLink;
        }
      })
      .addCase(updateLink.rejected, (state, action) => {
        state.operations.update.loading = false;
        state.operations.update.error = action.payload ?? null;
      })

      // Delete link
      .addCase(deleteLink.pending, (state) => {
        state.operations.delete.loading = true;
      })
      .addCase(deleteLink.fulfilled, (state, action) => {
        state.operations.delete.loading = false;
        const linkId = action.payload;

        // Remove from all structures
        delete state.entities[linkId];
        state.ids = state.ids.filter((id) => id !== linkId);
        state.links = state.links.filter((l) => linkKey(l) !== linkId);

        // Clear selection if needed
        if (state.selectedLink === linkId) {
          state.selectedLink = null;
        }
      })
      .addCase(deleteLink.rejected, (state, action) => {
        state.operations.delete.loading = false;
        state.operations.delete.error = action.payload ?? null;
      });
  },
});

// Export actions
export const {
  setSelectedLink,
  setLinks,
  setAllLinks,
  mergeFreshGroupLinks,
  updateLinkInList,
  removeLinkFromList,
  clearError,
  setCacheValidity,
  setLoading,
  setError,
} = linkSlice.actions;

// Export actions with linkActions namespace for compatibility
export const linkActions = linkSlice.actions;

// Selectors
export const selectLinks = (state: RootState) => state.links.links;
export const selectSelectedLink = (state: RootState) =>
  state.links.selectedLink;
export const selectLinksLoading = (state: RootState) => state.links.loading;
export const selectLinksError = (state: RootState) => state.links.error;
export const selectLinkEntities = (state: RootState) => state.links.entities;
export const selectLinkIds = (state: RootState) => state.links.ids;

// Cache validity selectors
export const selectLinksCacheValid = (state: RootState) =>
  state.links.cacheValid;
export const selectLinksLastFetched = (state: RootState) =>
  state.links.lastFetched;

// Cache TTL - 5 minutes
const LINKS_CACHE_TTL = 5 * 60 * 1000;

export const selectLinksCacheExpired = (state: RootState) => {
  const lastFetched = state.links.lastFetched;
  if (!lastFetched) return true;
  return Date.now() - lastFetched > LINKS_CACHE_TTL;
};

// Helper selector to determine if we should fetch links
export const selectShouldFetchLinks = (state: RootState) => {
  const hasLinks = state.links.links.length > 0;
  const cacheExpired = selectLinksCacheExpired(state);
  return !hasLinks || cacheExpired;
};

// Export reducer
export default linkSlice.reducer;
